import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * Whether the note a cart re-render just took out was open, so the copy it puts in can match.
 * null when there is nothing to carry over.
 *
 * A re-render that changes the cart's shape (the minimum cart fee row, a discount or a banner
 * appearing or going) can leave assets/morph.js unable to line this element up with the new
 * markup, so it swaps in a fresh <cart-note>. accordion-custom.js resets the fresh copy to its
 * default, which in the drawer is closed, and a note the customer had opened would snap shut.
 * @type {boolean | null}
 */
let carriedOpen = null;

/**
 * A custom element that displays a cart note.
 */
class CartNote extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    // Deferred to the end of the re-render: the old note can be removed after this one is added,
    // and accordion-custom (a child) applies its default after this callback runs.
    queueMicrotask(() => {
      const details = this.querySelector('details');
      if (carriedOpen === null || !details || !this.isConnected) return;
      details.open = carriedOpen;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const details = this.querySelector('details');
    if (!details) return;
    carriedOpen = details.open;
    // Nothing took its place (the cart emptied), so the next note starts from its default again.
    queueMicrotask(() => {
      if (!document.querySelector('cart-note')) carriedOpen = null;
    });
  }

  /**
   * Handles updates to the cart note.
   * @param {InputEvent} event - The input event in our text-area.
   */
  updateCartNote = debounce(async (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    const note = event.target.value;
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ note }),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('note-update:user-action', event);
    }
  }, 200);
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
